/**
 * Neirami — script di progetto (nr-scripts.js)
 * Incluso in layout/theme.liquid in coda, con defer, dopo gli script Glozin.
 * Pattern: IIFE + eventuale init su DOMContentLoaded; scope su .nr-* / custom elements.
 */
(function () {
  'use strict';

  function initNeirami() {
    initNrResponsiveVideo();
    initNrNewsletterModal();
    initNrNewsletterPopupOnce();
    defineNrShopTheLook();
    defineNrFlagshipStore();
    defineNrMegaHover();
    initNrNoNavLinks();
    initNrVariantMediaFilter();
    initNrKeepFilterLinks();
    initNrConsentGuard();
    initNrBackTopLift();
  }

  /**
   * «Torna su» dinamico: quando la barra in fondo al footer (copyright, dati
   * societari, pagamenti) entra nello schermo, il pulsante sale della parte
   * visibile della barra e si ferma sopra di essa. Così il footer non deve
   * riservare spazio vuoto in basso per non farsi coprire l'ultima riga.
   * Scrive solo la custom property --nr-back-top-lift (px) sul pulsante;
   * posizione e dimensioni restano in nr-style.css (sezione 16).
   */
  function initNrBackTopLift() {
    const button = document.querySelector('.footer back-to-top');
    const bar = document.querySelector('.footer .footer__bottom');
    if (!button || !bar) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const lift = Math.max(0, window.innerHeight - bar.getBoundingClientRect().top);
      button.style.setProperty('--nr-back-top-lift', Math.round(lift) + 'px');
    };
    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  /**
   * Consenso obbligatorio prima dell'invio: condizioni di vendita (carrello e
   * minicart) e privacy (form contatti). Glozin teneva il pulsante `disabled`
   * finché mancava la spunta: il click era muto, nessun feedback. Qui il
   * pulsante resta cliccabile (aria-disabled per le tecnologie assistive) e,
   * senza spunta, l'invio viene bloccato: compare il messaggio d'errore
   * (role="alert") e il focus va sulla checkbox; spuntando, il messaggio sparisce.
   * Markup: contenitore [data-nr-consent] con [data-nr-consent-input],
   * [data-nr-consent-error] e [data-nr-consent-submit]. Listener delegati su
   * document, così valgono anche per il minicart ridisegnato via AJAX.
   */
  function initNrConsentGuard() {
    function getParts(el) {
      var group = el.closest('[data-nr-consent]');
      if (!group) return null;
      return {
        input: group.querySelector('[data-nr-consent-input]'),
        error: group.querySelector('[data-nr-consent-error]'),
        submits: group.querySelectorAll('[data-nr-consent-submit]'),
      };
    }

    // theme.js (TermsConditions) rimette `disabled` quando si toglie la
    // spunta: lo si toglie di nuovo, altrimenti il click tornerebbe muto
    function sync(parts) {
      var accepted = !parts.input || parts.input.checked;
      parts.submits.forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', accepted ? 'false' : 'true');
      });
      if (accepted && parts.error) parts.error.hidden = true;
    }

    function guard(event, btn) {
      var parts = getParts(btn);
      if (!parts || !parts.input || parts.input.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (parts.error) parts.error.hidden = false;
      parts.input.focus();
    }

    document.querySelectorAll('[data-nr-consent-input]').forEach(function (input) {
      var parts = getParts(input);
      if (parts) sync(parts);
    });

    // Capture: si blocca prima di qualunque altro handler del tema
    document.addEventListener(
      'click',
      function (event) {
        var btn = event.target.closest('[data-nr-consent-submit]');
        if (btn) guard(event, btn);
      },
      true
    );
    // Invio con Invio da tastiera dentro il form: stesso controllo
    document.addEventListener(
      'submit',
      function (event) {
        var btn = event.submitter;
        if (btn && btn.matches('[data-nr-consent-submit]')) guard(event, btn);
      },
      true
    );
    document.addEventListener('change', function (event) {
      if (!event.target.matches('[data-nr-consent-input]')) return;
      var parts = getParts(event.target);
      if (parts) sync(parts);
    });
  }

  /**
   * Link della NR Collection heading che «portano con sé» un filtro
   * (data-nr-keep-filter="filter.p.vendor"): chi naviga tra le categorie con il
   * brand filtrato resta sul brand. I link riprendono sempre lo stato dell'URL
   * corrente: filtro aggiunto → aggiunto ai link, filtro tolto a mano → tolto
   * anche dai link. Glozin applica i filtri via AJAX + history.pushState senza
   * ridisegnare l'intestazione, quindi si riallinea a ogni cambio di URL
   * (pushState / replaceState / tasto indietro). Il Liquid stampa già i link
   * giusti al caricamento: questo script serve solo per i cambi successivi.
   */
  function initNrKeepFilterLinks() {
    var selector = 'a[data-nr-keep-filter][href]';
    if (!document.querySelector(selector)) return;

    function sync() {
      var current = new URLSearchParams(window.location.search);
      document.querySelectorAll(selector).forEach(function (link) {
        var param = link.getAttribute('data-nr-keep-filter');
        var url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.delete(param);
        current.getAll(param).forEach(function (value) {
          url.searchParams.append(param, value);
        });
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });
    }

    // Notifica i cambi di URL fatti via history API (filtri AJAX di collection.js)
    ['pushState', 'replaceState'].forEach(function (method) {
      var original = window.history[method];
      window.history[method] = function () {
        var result = original.apply(this, arguments);
        window.dispatchEvent(new Event('nr:urlchange'));
        return result;
      };
    });
    window.addEventListener('nr:urlchange', sync);
    window.addEventListener('popstate', sync);
    sync();
  }

  /**
   * Separazione immagini per variante colore (setting nr_variant_media_filter
   * della sezione Product information, configurazione da snippet
   * nr-variant-media-map). All'apertura si vedono tutte le immagini e, senza
   * ?variant= nell'URL, nessun colore è selezionato: «Aggiungi al carrello» resta
   * disattivato con «Seleziona un colore». Scelto un colore restano le sue
   * immagini + quelle senza colore; se il colore non ha immagini proprie si
   * vedono tutte. Il listener sta sul contenitore del prodotto, quindi gira DOPO
   * quello Glozin del variant picker (prezzo, pulsante, URL restano suoi).
   */
  function initNrVariantMediaFilter() {
    document.querySelectorAll('script[data-nr-media-filter]').forEach(function (configEl) {
      var config;
      try {
        config = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }
      var root = configEl.closest('.product__item-js') || document;
      var gallery = root.querySelector('media-gallery');
      if (!gallery) return;

      var map = config.map || {};
      var ownColors = {};
      Object.keys(map).forEach(function (id) {
        ownColors[map[id]] = true;
      });
      var idPrefix = config.section + '-';

      function mediaColor(el) {
        var holder = el.hasAttribute('data-media-id') ? el : el.querySelector('[data-media-id]');
        if (!holder) return '';
        var id = holder.getAttribute('data-media-id').replace(idPrefix, '');
        return map[id] || '';
      }

      // Caroselli: lista originale delle slide, per ricostruirle a ogni filtro
      var carousels = Array.prototype.map.call(gallery.querySelectorAll('.swiper'), function (el) {
        var wrapper = el.querySelector(':scope > .swiper-wrapper');
        return { el: el, wrapper: wrapper, slides: wrapper ? Array.prototype.slice.call(wrapper.children) : [] };
      }).filter(function (carousel) {
        return carousel.wrapper && carousel.slides.length;
      });

      function apply(colorKey) {
        var filtering = !!colorKey && !!ownColors[colorKey];
        function keep(el) {
          if (!filtering) return true;
          var c = mediaColor(el);
          return !c || c === colorKey;
        }

        // Griglia/stack (fuori dai caroselli): nascondi
        gallery.querySelectorAll('.media-gallery__image[data-media-id]').forEach(function (item) {
          if (item.closest('.swiper')) return;
          item.classList.toggle('nr-media-hidden', !keep(item));
        });

        // Caroselli con data-media-id: ricostruisci; le miniature seguono per indice
        var mask = null;
        carousels.forEach(function (carousel) {
          var hasIds = carousel.slides.some(function (slide) { return !!mediaColor(slide) || slide.matches('[data-media-id]') || slide.querySelector('[data-media-id]'); });
          var kept;
          if (hasIds) {
            var flags = carousel.slides.map(keep);
            mask = mask || flags;
            kept = carousel.slides.filter(function (_, i) { return flags[i]; });
          } else if (mask && mask.length === carousel.slides.length) {
            kept = carousel.slides.filter(function (_, i) { return mask[i]; });
          } else {
            return;
          }
          carousel.wrapper.replaceChildren.apply(carousel.wrapper, kept);
          if (carousel.el.swiper) {
            carousel.el.swiper.update();
            carousel.el.swiper.slideTo(0, 0);
          }
        });
      }

      function colorFieldset() {
        return Array.prototype.find.call(root.querySelectorAll('.nr-pdp-variants fieldset.product-form__input_color, variant-radios-detail fieldset.product-form__input_color'), function () { return true; });
      }

      function selectedColor() {
        var fieldset = colorFieldset();
        var checked = fieldset && fieldset.querySelector('input:checked');
        return checked ? checked.value.toLowerCase().trim() : '';
      }

      function requireColor() {
        ['#product-form-' + config.section, '#sticky-addcart-form-' + config.section].forEach(function (selector) {
          var form = document.querySelector(selector);
          var button = form && form.querySelector('[name="add"]');
          if (!button) return;
          button.setAttribute('disabled', 'disabled');
          var label = button.querySelector(':scope > span');
          if (label && config.selectLabel) label.textContent = config.selectLabel;
        });
        // Glozin nasconde il prezzo quando la variante non si risolve: qui è solo «colore da scegliere»
        var price = document.getElementById('price-' + config.section);
        if (price) price.classList.remove('hidden');
      }

      root.addEventListener('change', function (event) {
        if (!event.target.closest || !event.target.closest('variant-radios-detail, .nr-pdp-variants')) return;
        var color = selectedColor();
        apply(color);
        if (!color && config.noPreselect) requireColor();
      });

      var initial = selectedColor();
      apply(initial);
      if (!initial && config.noPreselect) requireColor();
    });
  }

  /**
   * Voci di menu segnaposto (href="#", es. Brand, About): aprono solo il mega
   * menu, niente navigazione né salto in cima alla pagina (task Boro 471).
   */
  function initNrNoNavLinks() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[data-nr-no-nav]');
      if (link) event.preventDefault();
    });
  }

  /**
   * Mega menu desktop: hover su una voce di primo livello mostra le
   * sottovoci nella colonna centrale. Lo stato resta finché non si passa
   * a un'altra voce (così si può raggiungere i link del pannello).
   */
  function defineNrMegaHover() {
    if (customElements.get('nr-mega-hover')) return;

    customElements.define(
      'nr-mega-hover',
      class extends HTMLElement {
        connectedCallback() {
          this.levels = this.querySelectorAll('[data-nr-mega-level]');
          this.panels = this.querySelectorAll('[data-nr-mega-panel]');
          if (!this.levels.length) return;

          // Nessuna voce preselezionata: la colonna centrale compare solo all'hover
          this._activeIndex = -1;
          this._pendingIndex = -1;
          this.panels.forEach(function (panel) {
            panel.setAttribute('inert', '');
          });
          var self = this;

          // Chiudendo il mega menu si azzera la selezione: alla riapertura
          // non resta l'ultima voce scelta (task Boro 471)
          var host = this.closest('.menu-parent') || this;
          host.addEventListener('mouseleave', function () {
            self.reset();
          });
          host.addEventListener('focusout', function (event) {
            if (!host.contains(event.relatedTarget)) self.reset();
          });
          // L indice viene dal markup: le voci senza sottovoci non hanno pannello,
          // quindi la posizione nella NodeList non corrisponde più alla voce.
          this.levels.forEach(function (level) {
            var index = Number(level.dataset.nrMegaLevel);
            level.addEventListener('mouseenter', function () {
              self.activate(index);
            });
            level.addEventListener('focus', function () {
              self.activate(index);
            });
          });
        }

        reset() {
          clearTimeout(this._fadeTimer);
          this._pendingIndex = -1;
          this._activeIndex = -1;
          this.levels.forEach(function (level) {
            level.classList.remove('is-active');
            level.setAttribute('aria-expanded', 'false');
          });
          this.panels.forEach(function (panel) {
            panel.classList.remove('is-active');
            panel.setAttribute('inert', '');
          });
        }

        activate(index) {
          if (this._pendingIndex === index) return;
          this._pendingIndex = index;

          var self = this;
          this.levels.forEach(function (level, i) {
            var on = i === index;
            level.classList.toggle('is-active', on);
            level.setAttribute('aria-expanded', on ? 'true' : 'false');
          });

          this.panels.forEach(function (panel) {
            panel.classList.remove('is-active');
            panel.setAttribute('inert', '');
          });

          clearTimeout(this._fadeTimer);
          this._fadeTimer = setTimeout(function () {
            self.panels.forEach(function (panel) {
              var on = Number(panel.dataset.nrMegaPanel) === index;
              panel.classList.toggle('is-active', on);
              if (on) {
                panel.removeAttribute('inert');
              } else {
                panel.setAttribute('inert', '');
              }
            });
            self._activeIndex = index;
          }, 280);
        }
      }
    );
  }

  /**
   * Popup newsletter all'atterraggio = la modale newsletter del footer
   * (blocco nr_menu), così esiste un solo modulo con un solo testo. Il popup
   * Glozin (newsletter-popup, gruppo overlay) è spento da editor.
   * - una volta per SESSIONE: all'apertura automatica si scrive il cookie
   *   `newsletter_popup` senza scadenza (cookie di sessione);
   * - «Non mostrare più» (visibile solo nell'apertura automatica) = 30 giorni;
   * - dopo l'iscrizione riuscita non si ripropone per un anno.
   */
  function initNrNewsletterPopupOnce() {
    if (window.Shopify && Shopify.designMode) return;

    var dialog = document.querySelector('dialog.nr-newsletter-modal');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    var dismiss = dialog.querySelector('[data-nr-newsletter-dismiss]');

    function setCookie(value, maxAge) {
      document.cookie =
        'newsletter_popup=' + value + '; path=/; SameSite=Lax' + (maxAge ? '; max-age=' + maxAge : '');
    }

    if (dismiss) {
      dismiss.addEventListener('click', function () {
        setCookie('30', 30 * 24 * 60 * 60);
        dialog.close();
      });
    }
    // Riaperta a mano dal footer, il link non serve
    dialog.addEventListener('close', function () {
      if (dismiss) dismiss.hidden = true;
    });

    if (dialog.querySelector('[data-nr-newsletter-success]')) {
      setCookie('subscribed', 365 * 24 * 60 * 60);
      return;
    }
    if (document.cookie.indexOf('newsletter_popup=') !== -1) return;

    // Breve attesa per il primo render della pagina
    setTimeout(function () {
      if (dialog.open) return;
      if (dismiss) dismiss.hidden = false;
      dialog.showModal();
      setCookie('1');
    }, 800);
  }

  /**
   * Gallerie Flagship store: apertura a schermo intero con PhotoSwipe.
   * I moduli ESM del tema (photoswipe*.esm.min.js) vengono importati solo al
   * primo click — la pagina non paga il lightbox finché non serve. Se
   * l'import fallisce resta il fallback nativo: i link aprono l'immagine
   * piena in un nuovo tab.
   */
  function defineNrFlagshipStore() {
    if (customElements.get('nr-flagship-store')) return;

    customElements.define(
      'nr-flagship-store',
      class extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', this.onPhotoClick.bind(this));
        }

        onPhotoClick(event) {
          var link = event.target.closest('a.nr-flagship__photo');
          if (!link) return;
          event.preventDefault();

          var gallery = link.closest('[data-nr-gallery]');
          var links = Array.prototype.slice.call(
            gallery.querySelectorAll('a.nr-flagship__photo')
          );
          var index = links.indexOf(link);
          var self = this;

          Promise.all([
            import(this.dataset.pswpLightboxSrc),
            import(this.dataset.pswpCoreSrc),
          ])
            .then(function (modules) {
              var PhotoSwipeLightbox = modules[0].default;
              var PhotoSwipe = modules[1].default;

              // Un'istanza per galleria, riusata ai click successivi
              if (!self._lightboxes) self._lightboxes = new Map();
              var lightbox = self._lightboxes.get(gallery);
              if (!lightbox) {
                lightbox = new PhotoSwipeLightbox({
                  gallery: gallery,
                  children: 'a.nr-flagship__photo',
                  pswpModule: PhotoSwipe,
                  bgOpacity: 0.9,
                  counter: false,
                  zoom: false,
                });
                lightbox.init();
                self._lightboxes.set(gallery, lightbox);
              }
              lightbox.loadAndOpen(index);
            })
            .catch(function () {
              // Fallback: comportamento nativo del link
              window.open(link.href, '_blank', 'noopener');
            });
        }
      }
    );
  }

  /**
   * Sorgente video per dispositivo nei blocchi _nr-media-content.
   * Il markup non ha src quando esiste anche il taglio mobile: così il telefono
   * non scarica il video desktop prima dello swap. Se il video mobile manca, il
   * blocco ha già il src desktop server-side e qui non entra proprio.
   */
  function initNrResponsiveVideo() {
    var videos = document.querySelectorAll('video[data-nr-video-desktop]');
    if (!videos.length) return;

    var isMobile = window.matchMedia('(max-width: 767px)').matches;

    videos.forEach(function (video) {
      var src = isMobile
        ? video.getAttribute('data-nr-video-mobile')
        : video.getAttribute('data-nr-video-desktop');
      if (!src || video.src) return;
      video.src = src;
    });
  }

  /**
   * Modale newsletter footer (blocco nr_menu): <dialog> nativo, trigger data-nr-newsletter-open.
   */
  function initNrNewsletterModal() {
    var triggers = document.querySelectorAll('[data-nr-newsletter-open]');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      var targetId = trigger.getAttribute('data-nr-newsletter-target');
      if (!targetId) return;

      var dialog = document.getElementById(targetId);
      if (!dialog || dialog.tagName !== 'DIALOG') return;

      var previousFocus = null;

      function openDialog() {
        previousFocus = trigger;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        }
        var focusTarget =
          dialog.querySelector('[data-nr-newsletter-close]') ||
          dialog.querySelector('input, button, textarea, select');
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus();
        }
      }

      function closeDialog() {
        if (typeof dialog.close === 'function' && dialog.open) {
          dialog.close();
        }
        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
      }

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openDialog();
      });

      dialog.querySelectorAll('[data-nr-newsletter-close]').forEach(function (closeBtn) {
        closeBtn.addEventListener('click', closeDialog);
      });

      dialog.addEventListener('click', function (event) {
        var rect = dialog.getBoundingClientRect();
        var isInDialog =
          rect.top <= event.clientY &&
          event.clientY <= rect.top + rect.height &&
          rect.left <= event.clientX &&
          event.clientX <= rect.left + rect.width;
        if (!isInDialog) {
          closeDialog();
        }
      });

      dialog.addEventListener('cancel', function () {
        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
      });

      // Riapre la modale dopo submit con successo (form customer)
      if (dialog.querySelector('[data-nr-newsletter-success]')) {
        openDialog();
        var successEl = dialog.querySelector('[data-nr-newsletter-success]');
        if (successEl && typeof successEl.focus === 'function') {
          successEl.focus();
        }
      }
    });
  }

  /**
   * Shop the Look: multi-add via /cart/add.js (items[]) + aggiornamento minicart Glozin.
   */
  function defineNrShopTheLook() {
    if (customElements.get('nr-shop-the-look')) return;

    customElements.define(
      'nr-shop-the-look',
      class NrShopTheLook extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;
          this.errorEl = this.querySelector('[data-nr-shop-look-error]');
          this.submitBtn = this.querySelector('[data-nr-shop-look-submit]');
          this.listEl = this.querySelector('[data-nr-shop-look-list]');
          if (!this.submitBtn || !this.listEl) return;

          this.listEl.addEventListener('change', this.onSizeChange.bind(this));
          this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
        }

        onSizeChange(event) {
          var input = event.target;
          if (!input || !input.classList.contains('nr-shop-look__size-input')) return;
          var item = input.closest('.nr-shop-look__item');
          if (!item) return;
          item.querySelectorAll('.nr-shop-look__size').forEach(function (label) {
            label.classList.remove('is-active');
          });
          var label = item.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.add('is-active');
          this.clearError();
        }

        clearError() {
          if (!this.errorEl) return;
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }

        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent = message || '';
          this.errorEl.hidden = !message;
        }

        collectItems() {
          var items = [];
          var missing = [];
          var unavailable = [];
          var rows = this.listEl.querySelectorAll('.nr-shop-look__item');

          rows.forEach(function (row) {
            if (row.getAttribute('data-soldout') === 'true') return;

            var title = row.getAttribute('data-product-title') || '';
            var checked = row.querySelector(
              '.nr-shop-look__size-input:checked:not([disabled])'
            );
            if (!checked) {
              checked = row.querySelector(
                'input[type="hidden"].nr-shop-look__size-input:not([disabled])'
              );
            }

            if (!checked) {
              missing.push(title);
              return;
            }

            var available = checked.getAttribute('data-available');
            if (available === 'false') {
              unavailable.push(title);
              return;
            }

            var id = parseInt(checked.value, 10);
            if (!id) {
              missing.push(title);
              return;
            }

            items.push({ id: id, quantity: 1 });
          });

          return { items: items, missing: missing, unavailable: unavailable };
        }

        onSubmit(event) {
          event.preventDefault();
          this.clearError();

          var collected = this.collectItems();
          var strings = (window.nrShopTheLookStrings) || {};

          if (collected.missing.length) {
            this.showError(
              strings.select_size ||
                'Please select a size for every available item.'
            );
            return;
          }

          if (collected.unavailable.length) {
            this.showError(
              strings.unavailable ||
                'One or more selected sizes are unavailable.'
            );
            return;
          }

          if (!collected.items.length) {
            this.showError(
              strings.no_items || 'No available items to add.'
            );
            return;
          }

          this.addItems(collected.items);
        }

        addItems(items) {
          var self = this;
          var cart =
            document.querySelector('cart-notification') ||
            document.querySelector('cart-drawer');
          var btn = this.submitBtn;
          var addUrl =
            (window.routes && window.routes.cart_add_url
              ? window.routes.cart_add_url
              : '/cart/add') + '.js';

          var body = { items: items };
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.sections = cart
              .getSectionsToRender()
              .map(function (section) {
                return section.id;
              });
            body.sections_url = window.location.pathname;
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
          }

          btn.classList.add('loading');
          btn.setAttribute('disabled', 'disabled');

          fetch(addUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          })
            .then(function (response) {
              return response.json().then(function (data) {
                return { ok: response.ok, status: response.status, data: data };
              });
            })
            .then(function (result) {
              var data = result.data || {};
              if (!result.ok || data.status) {
                self.showError(
                  data.description ||
                    data.message ||
                    ((window.nrShopTheLookStrings || {}).generic_error) ||
                    'Unable to add items to cart.'
                );
                return;
              }
              return self.onAddSuccess(cart, data);
            })
            .catch(function () {
              var strings = window.nrShopTheLookStrings || {};
              self.showError(
                strings.generic_error || 'Unable to add items to cart.'
              );
            })
            .finally(function () {
              btn.classList.remove('loading');
              btn.removeAttribute('disabled');
            });
        }

        onAddSuccess(cart, parsedState) {
          return fetch('/cart.json')
            .then(function (res) {
              return res.json();
            })
            .then(function (cartJson) {
              if (cartJson.item_count != undefined) {
                document.querySelectorAll('.cart-count').forEach(function (el) {
                  if (el.classList.contains('cart-count-drawer')) {
                    el.innerHTML = '(' + cartJson.item_count + ')';
                  } else {
                    el.innerHTML =
                      cartJson.item_count > 100 ? '~' : cartJson.item_count;
                  }
                });
                if (document.querySelector('header-total-price')) {
                  document
                    .querySelector('header-total-price')
                    .updateTotal(cartJson);
                }
                var freeShip = document.querySelector('free-ship-progress-bar');
                if (freeShip) {
                  freeShip.init(cartJson.items_subtotal_price);
                }
              }

              if (
                cart &&
                parsedState.sections &&
                typeof cart.getSectionsToRender === 'function'
              ) {
                cart.getSectionsToRender().forEach(function (section) {
                  var elementToReplace = document.getElementById(section.id);
                  if (!elementToReplace || !parsedState.sections[section.id]) {
                    return;
                  }
                  var html = new DOMParser().parseFromString(
                    parsedState.sections[section.id],
                    'text/html'
                  );
                  var minicartForm = html.querySelector('#minicart-form');
                  if (minicartForm) {
                    elementToReplace.innerHTML = minicartForm.innerHTML;
                  }
                  var countdown = cart.querySelector('.cart-countdown-time');
                  var htmlCountdown = html.querySelector('.cart-countdown-time');
                  if (countdown && htmlCountdown) {
                    countdown.innerHTML = htmlCountdown.innerHTML;
                  }
                });
                if (typeof cart.cartAction === 'function') {
                  cart.cartAction();
                }
              }

              if (typeof window.showMinicartAddSuccessMessage === 'function') {
                window.showMinicartAddSuccessMessage(cart);
              }
              if (cart && typeof cart.open === 'function') {
                cart.open();
              }
            });
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNeirami);
  } else {
    initNeirami();
  }
})();
